import React from 'react';
import { useNavigation } from '@react-navigation/native';

import GetPrimeBookPages from '@src/api/GetPrimeBookPages';
import { useAddOrReturnBook } from '@src/database/hooks';
import { generatePrimePage } from '@src/factories';
import useAxiosErrorHandler from '@src/hooks/useAxiosErrorHandler';
import useAxiosRequestConfig from '@src/hooks/useAxiosRequestConfig';
import useAllStatus from '@src/hooks/useAllStatus';
import { PrimeBook } from '@src/types';

const limit = 25;

const useDownloadPages = (data?: PrimeBook) => {
  const axiosRequestConfig = useAxiosRequestConfig();
  const axiosErrorHandler = useAxiosErrorHandler();

  const allStatus = useAllStatus();
  const navigation = useNavigation();

  const addOrReturnBook = useAddOrReturnBook();
  const [progress, setProgress] = React.useState(-1);

  const handleDownload = React.useCallback(async () => {
    if (!data) {
      return;
    }

    if (
      allStatus === undefined ||
      allStatus[0]?.daysRemaining === undefined ||
      (allStatus[0]?.daysRemaining === 0 && allStatus[1]?.daysRemaining === 0)
    ) {
      navigation.navigate('PrimePlans', { primeId: data.id });
      return;
    }

    const book = await addOrReturnBook(data);

    if (book.isDownloaded) {
      navigation.navigate('PrimeBookMenu', { id: book.id });
      return;
    }
    setProgress(0);

    try {
      for (let i = 0; i < book.pagesCount; i = i + limit) {
        const primePages = await GetPrimeBookPages(
          data.id,
          i,
          limit,
          axiosRequestConfig
        );
        await book.addPages(
          primePages.data.pages.map((page, index) => {
            return generatePrimePage(page, i + index);
          })
        );
        setProgress((i / book.pagesCount) * 100);
      }
    } catch (e) {
      axiosErrorHandler(e);
    }
    await book.setDownloaded(true);
    setProgress(-1);
    navigation.navigate('PrimeBookMenu', { id: book.id });
  }, [
    addOrReturnBook,
    axiosErrorHandler,
    axiosRequestConfig,
    data,
    allStatus,
    navigation,
  ]);

  return React.useMemo(() => ({ loading: progress, handleDownload }), [
    handleDownload,
    progress,
  ]);
};

export default useDownloadPages;
